<!--
通话记录页面组件
被主导航调用
权限点：call.view
简要说明：用于展示和管理所有通话记录，导出、详情、播放等操作需加权限判断
-->
<template>
  <div class="call-records">
    <!-- 统计卡片 -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon">
          <el-icon><Phone /></el-icon>
        </div>
        <div class="stat-content">
          <div class="stat-number">{{ statsData.totalCalls }}</div>
          <div class="stat-label">总通话次数</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon">
          <el-icon><Check /></el-icon>
        </div>
        <div class="stat-content">
          <div class="stat-number">{{ statsData.connectedCalls }}</div>
          <div class="stat-label">接通次数</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon">
          <el-icon><Close /></el-icon>
        </div>
        <div class="stat-content">
          <div class="stat-number">{{ statsData.missedCalls }}</div>
          <div class="stat-label">未接通</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon">
          <el-icon><Timer /></el-icon>
        </div>
        <div class="stat-content">
          <div class="stat-number">{{ statsData.totalDuration }}</div>
          <div class="stat-label">总通话时长</div>
        </div>
      </div>
    </div>

    <!-- 搜索筛选区域 -->
    <div class="search-section">
      <el-card class="search-card">
        <div class="search-form">
          <el-row :gutter="20">
            <el-col :span="5" v-if="!device_id">
              <el-input
                v-model="searchForm.deviceName"
                placeholder="设备名称"
                clearable
              >
                <template #prefix>
                  <el-icon><Search /></el-icon>
                </template>
              </el-input>
            </el-col>
            <el-col :span="3">
              <el-select
                v-model="searchForm.status"
                placeholder="通话状态"
                clearable
              >
                <el-option label="全部" value="" />
                <el-option label="进行中" value="active" />
                <el-option label="已完成" value="ended" />
                <el-option label="取消(未接通)" value="cancelled" />
                <el-option label="系统超时" value="system_timeout" />
                <el-option label="管理员结束" value="admin_forced_end" />
                <el-option label="失败" value="failed" />
              </el-select>
            </el-col>
            <!-- 租户筛选（仅 super_admin 可见） -->
            <el-col :span="4" v-if="isSuperAdmin">
              <el-select
                v-model="searchForm.tenantId"
                placeholder="租户"
                clearable
                @visible-change="onTenantDropdownVisible"
              >
                <el-option
                  v-for="t in tenants"
                  :key="t.id"
                  :label="t.name"
                  :value="t.id"
                />
              </el-select>
            </el-col>
            <!-- 处理人员筛选，仅admin和superadmin可见 -->
            <el-col :span="3" v-if="canFilterByContactName">
              <el-input
                v-model="searchForm.contactName"
                placeholder="处理人员"
                clearable
              >
                <template #prefix>
                  <el-icon><User /></el-icon>
                </template>
              </el-input>
            </el-col>
            <el-col :span="7">
              <el-date-picker
                v-model="searchForm.dateRange"
                type="datetimerange"
                range-separator="至"
                start-placeholder="开始时间"
                end-placeholder="结束时间"
                format="YYYY-MM-DD HH:mm:ss"
                value-format="YYYY-MM-DD HH:mm:ss"
                @change="handleDateChange"
              />
            </el-col>
            <el-col :span="6">
              <div class="button-group">
                <el-button type="primary" @click="searchRecords">
                  <el-icon><Search /></el-icon>
                  搜索
                </el-button>
                <el-button @click="resetSearch">
                  <el-icon><Refresh /></el-icon>
                  重置
                </el-button>
              </div>
            </el-col>
          </el-row>
        </div>
      </el-card>
    </div>

    <!-- 主内容区域：左侧表格，右侧图表 -->
    <div class="main-content">
      <!-- 底部：通话记录列表（移除标题行） -->
      <div class="records-section">
        <el-card class="records-card">
          <el-table
            :data="records"
            style="width: 100%"
            v-loading="loading"
            :height="recordsTableHeight"
          >
            <el-table-column
              prop="device_id"
              label="设备编号"
              min-width="200"
              show-overflow-tooltip
              :class-name="'nowrap-cell table-th-compact'"
            />
            <!-- 租户名称（仅 super_admin 可见） -->
            <el-table-column
              v-if="isSuperAdmin"
              prop="tenant_name"
              label="租户名称"
              min-width="160"
              :class-name="'table-th-compact'"
            />
            <el-table-column
              label="通话时间"
              min-width="200"
              :class-name="'table-th-compact'"
            >
              <template #default="scope">
                {{ formatDateTime(scope.row.start_time) }}
              </template>
            </el-table-column>
            <el-table-column
              prop="status"
              label="通话状态"
              min-width="120"
              :class-name="'table-th-compact'"
            >
              <template #default="scope">
                <el-tag :type="getStatusType(scope.row.status)">
                  {{ getStatusText(scope.row.status) }}
                </el-tag>
              </template>
            </el-table-column>
            <!-- 根据用户角色显示contact名字 -->
            <el-table-column
              v-if="showContactName"
              prop="contact_name"
              label="处理人员"
              min-width="150"
              :class-name="'table-th-compact'"
            >
              <template #default="scope">
                {{ getDisplayName(scope.row.contact_name) }}
              </template>
            </el-table-column>
            <el-table-column
              label="通话时长"
              min-width="120"
              :class-name="'table-th-compact'"
            >
              <template #default="scope">
                {{ formatDuration(scope.row.start_time, scope.row.end_time) }}
              </template>
            </el-table-column>
            <!-- 移除重复的"接入人员"列 -->
            <el-table-column label="操作" min-width="250" align="center">
              <template #default="scope">
                <div class="action-buttons-center">
                  <el-button
                    type="primary"
                    size="small"
                    @click="viewRecord(scope.row)"
                    v-if="hasPermission('call.view')"
                    style="padding: 4px 8px; font-size: 12px"
                  >
                    详情
                  </el-button>
                  <el-button
                    type="info"
                    size="small"
                    @click="playRecord(scope.row)"
                    v-if="hasPermission('call.view')"
                    style="padding: 4px 8px; font-size: 12px"
                  >
                    播放
                  </el-button>
                  <el-button
                    type="success"
                    size="small"
                    @click="showDeviceStats(scope.row)"
                    v-if="hasPermission('call.view')"
                    style="padding: 4px 8px; font-size: 12px"
                  >
                    本月统计
                  </el-button>
                </div>
              </template>
            </el-table-column>
          </el-table>

          <div class="table-footer">
            <el-pagination
              v-model:current-page="currentPage"
              v-model:page-size="pageSize"
              :page-sizes="[10, 20, 50, 100]"
              :total="totalRecords"
              layout="total, sizes, prev, pager, next, jumper"
            />
          </div>
        </el-card>
      </div>

      <!-- 月度通话时间图表（无标题行，右侧加入导出按钮） -->
      <div class="chart-section">
        <el-card class="chart-card">
          <div class="chart-container">
            <div ref="chartRef" class="chart"></div>
            <div class="chart-actions">
              <el-button type="primary" size="small" @click="exportRecords">
                <el-icon><Download /></el-icon>
                导出记录
              </el-button>
            </div>
          </div>
        </el-card>
      </div>
    </div>
  </div>

  <!-- 播放对话框 -->
  <el-dialog
    v-model="playDialogVisible"
    title="播放通话录音"
    width="500px"
    :close-on-click-modal="false"
    :close-on-press-escape="true"
  >
    <div class="audio-player-container">
      <div class="audio-info">
        <h4>{{ currentAudio.title }}</h4>
      </div>
      <div class="audio-controls">
        <audio
          ref="audioPlayer"
          controls
          preload="metadata"
          style="width: 100%"
        >
          <source :src="currentAudio.url || ''" type="audio/mpeg" />
          Your browser does not support the audio element.
        </audio>
      </div>
    </div>
    <template #footer>
      <div class="dialog-footer">
        <el-button @click="playDialogVisible = false">关闭</el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script lang="ts" setup>
import {
  ref,
  computed,
  onMounted,
  onUnmounted,
  reactive,
  watch,
  nextTick,
} from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import {
  Phone,
  Check,
  Close,
  Timer,
  Search,
  Refresh,
  Download,
  ArrowLeft,
  User,
} from "@element-plus/icons-vue";
import { useStore } from "vuex";
import apiClient from "../services/apiClient";
import config from "@/config";
import { logger } from "../utils/logger";
import {
  DataService,
  UserServiceApi,
  TenantServiceApi,
} from "../services/dataService";
import { useRoute, useRouter } from "vue-router";
import {
  ROLE_OPERATOR,
  ROLE_ADMIN,
  ROLE_SUPER_ADMIN,
} from "../types/permission";
import { formatDateTime } from "../utils/dateTimeUtils";
import * as echarts from "echarts";

const store = useStore();
const hasPermission = (perm: string) => store.getters.hasPermission(perm);
const route = useRoute();
const router = useRouter();

// 响应式状态定义
const loading = ref(false); // 控制加载状态
const records = ref([]); // 存储从后端获取的通话记录
const totalRecords = ref(0); // 总记录数，用于分页
// 用户显示名映射（username -> display_name）
const userDisplayMap = ref<Record<string, string>>({});
const displayToUserMap = ref<Record<string, string>>({});
// 租户选项（仅 super_admin 使用）
const tenants = ref<{ id: string | number; name: string }[]>([]);

// 图表相关状态
const chartRef = ref<HTMLElement>();
const chartInstance = ref<echarts.ECharts>();
const chartLoading = ref(false);
const chartData = reactive<Array<{ date: string; duration: number }>>([]);
// 年月显示（置于标题旁）
const currentYM = ref<string>("");
// 表格高度：窗口高度减去顶部块与图表区域的估算高度
const recordsTableHeight = computed(() => {
  // 视口高度 - 顶部统计 - 筛选区(更紧凑) - 图表卡片(300) - 其他边距
  const h = window.innerHeight - 110 - 70 - 300 - 70;
  // 提高最小高度以容纳更多行
  return Math.max(360, h);
});
// 播放对话框与引用
const playDialogVisible = ref(false);
const audioPlayer = ref<HTMLAudioElement | null>(null);
const currentAudio = reactive<{ url: string | null; title: string | null }>({
  url: null,
  title: null,
});

// 搜索表单
const searchForm = reactive({
  deviceName: "",
  status: "",
  dateRange: [] as string[],
  startTime: "",
  endTime: "",
  contactName: "", // 新增处理人员筛选字段
  tenantId: "", // super_admin 的租户筛选
});

// 分页
const currentPage = ref(1);
const pageSize = ref(20);

// 顶部的统计数据
const statsData = reactive({
  totalCalls: 0,
  connectedCalls: 0,
  missedCalls: 0,
  totalDuration: "0h 0m",
});

// 图表描述文本
const chartDescription = computed(() => {
  const deviceText = searchForm.deviceName
    ? `${searchForm.deviceName} 每日通话时长`
    : "所有设备每日通话时长";

  return deviceText;
});

// 获取月度通话时间统计数据
const fetchMonthlyCallStats = async () => {
  chartLoading.value = true;
  try {
    const currentUser = store.getters.getUser;
    const userRole = currentUser?.role || "";
    const userId = currentUser?.id;

    const params = {
      deviceName: searchForm.deviceName || undefined,
      user_role: userRole,
      user_id: userId,
    };

    logger.info("获取月度通话时间统计数据:", params);

    const response = await DataService.getMonthlyCallStats(params);
    const responseData = (response as any).data || response;
    const { code, data, message } = responseData;

    if (code === 0 && data) {
      // 验证数据结构
      const dailyStats = data.dailyStats || [];
      if (Array.isArray(dailyStats)) {
        // 清理和验证数据
        const validStats = dailyStats.filter(
          (item: any) =>
            item &&
            typeof item.date === "string" &&
            typeof item.duration === "number" &&
            !isNaN(item.duration) &&
            item.duration >= 0
        );

        // 清空现有数据并添加新数据
        chartData.splice(0, chartData.length);
        chartData.push(...validStats);

        // 设置当前年月展示
        if (validStats.length > 0 && typeof validStats[0].date === "string") {
          currentYM.value = String(validStats[0].date).slice(0, 7);
        } else {
          const now = new Date();
          currentYM.value = `${now.getFullYear()}-${String(
            now.getMonth() + 1
          ).padStart(2, "0")}`;
        }

        logger.info("月度通话时间统计数据获取成功:", validStats);
        logger.info("原始数据:", dailyStats);
        logger.info("设备筛选:", searchForm.deviceName);
        updateChart();
      } else {
        logger.error("月度统计数据格式错误:", data);
        ElMessage.error("数据格式错误");
        chartData.splice(0, chartData.length);
        updateChart();
      }
    } else {
      logger.error("获取月度通话时间统计数据失败:", message);
      ElMessage.error(message || "获取月度统计数据失败");
      chartData.splice(0, chartData.length);
      updateChart();
    }
  } catch (error) {
    logger.error("获取月度通话时间统计数据时发生错误:", error);
    ElMessage.error("网络错误，无法加载月度统计数据");
    chartData.splice(0, chartData.length);
    updateChart();
  } finally {
    chartLoading.value = false;
  }
};

// 初始化图表
const initChart = () => {
  if (chartRef.value) {
    chartInstance.value = echarts.init(chartRef.value);

    // 监听窗口大小变化
    window.addEventListener("resize", () => {
      chartInstance.value?.resize();
    });
  }
};

// 更新图表
const updateChart = () => {
  if (!chartInstance.value) return;

  try {
    // 数据验证和清理
    const validData = chartData.filter(
      (item) =>
        item &&
        typeof item.date === "string" &&
        typeof item.duration === "number" &&
        !isNaN(item.duration) &&
        item.duration >= 0
    );

    if (validData.length === 0) {
      // 如果没有有效数据，显示空状态
      const option = {
        title: {
          text: "本月每日累计通话时长",
          left: "center",
          textStyle: {
            fontSize: 16,
            fontWeight: "bold",
          },
        },
        graphic: {
          type: "text",
          left: "center",
          top: "middle",
          style: {
            text: "暂无数据",
            fontSize: 14,
            fill: "#999",
          },
        },
      };
      try {
        chartInstance.value.setOption(option, true);
      } catch (chartError) {
        logger.error("设置图表空状态时出错:", chartError);
      }
      return;
    }

    const dates = validData.map((item) => item.date);
    const durations = validData.map((item) => item.duration);

    // 验证数据完整性
    if (dates.length !== durations.length) {
      logger.error("图表数据不匹配，日期和时长数组长度不一致");
      return;
    }

    // 顶部副标题显示 年-月
    const ymFromData =
      validData && validData.length > 0 && typeof validData[0].date === "string"
        ? String(validData[0].date).slice(0, 7)
        : `${new Date().getFullYear()}-${String(
            new Date().getMonth() + 1
          ).padStart(2, "0")}`;

    const option = {
      title: {
        text: currentYM.value || "",
        left: "center",
        textStyle: {
          fontSize: 28, // 与顶部统计数字接近
          fontWeight: "bold",
        },
      },
      tooltip: {
        trigger: "item",
        axisPointer: { type: "shadow" },
        formatter: function (params: any) {
          if (!params) return "";
          const seconds = typeof params.value === "number" ? params.value : 0;
          const hours = Math.floor(seconds / 3600);
          const minutes = Math.floor((seconds % 3600) / 60);
          const remainingSeconds = seconds % 60;

          let timeText = "";
          if (hours > 0) timeText += `${hours}小时`;
          if (minutes > 0) timeText += `${minutes}分钟`;
          if (remainingSeconds > 0 || (hours === 0 && minutes === 0))
            timeText += `${remainingSeconds}秒`;
          return `累计通话时长: ${timeText} (${seconds}秒)`;
        },
      },
      xAxis: {
        type: "category",
        // 横轴仅显示日
        data: dates.map((d) =>
          typeof d === "string" && d.length >= 10 ? d.slice(8, 10) : d
        ),
        axisLabel: {
          rotate: 0,
          fontSize: 12,
          margin: 8,
        },
        name: "日",
        nameLocation: "end",
        nameTextStyle: { fontSize: 12, color: "#666" },
      },
      yAxis: {
        type: "value",
        name: "累计通话时长(秒)",
        axisLabel: {
          formatter: function (value: number) {
            if (typeof value !== "number" || isNaN(value)) return "0s";
            const hours = Math.floor(value / 3600);
            const minutes = Math.floor((value % 3600) / 60);
            if (hours > 0) {
              return `${hours}h${minutes}m`;
            } else if (minutes > 0) {
              return `${minutes}m`;
            } else {
              return `${value}s`;
            }
          },
        },
      },
      series: [
        {
          name: "通话时长",
          type: "bar",
          data: durations.map((duration, index) => ({
            value: duration,
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: "#83bff6" },
                { offset: 0.5, color: "#188df0" },
                { offset: 1, color: "#188df0" },
              ]),
            },
          })),
          emphasis: {
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: "#2378f7" },
                { offset: 0.7, color: "#2378f7" },
                { offset: 1, color: "#2378f7" },
              ]),
            },
          },
        },
      ],
      grid: {
        left: "10%",
        right: "10%",
        bottom: "20%",
        top: "15%",
      },
    };

    // 使用try-catch包装setOption调用
    try {
      chartInstance.value.setOption(option, true);
    } catch (chartError) {
      logger.error("设置图表选项时出错:", chartError);
      // 尝试显示错误状态
      try {
        chartInstance.value.setOption(
          {
            title: {
              text: "图表加载失败",
              left: "center",
              textStyle: {
                fontSize: 16,
                fontWeight: "bold",
                color: "#ff4d4f",
              },
            },
            graphic: {
              type: "text",
              left: "center",
              top: "middle",
              style: {
                text: "图表渲染失败，请刷新页面重试",
                fontSize: 14,
                fill: "#ff4d4f",
              },
            },
          },
          true
        );
      } catch (fallbackError) {
        logger.error("设置错误状态图表时也失败:", fallbackError);
      }
    }
  } catch (error) {
    logger.error("更新图表时发生错误:", error);
    // 尝试显示错误状态
    try {
      if (chartInstance.value) {
        chartInstance.value.setOption(
          {
            title: {
              text: "图表加载失败",
              left: "center",
              textStyle: {
                fontSize: 16,
                fontWeight: "bold",
                color: "#ff4d4f",
              },
            },
            graphic: {
              type: "text",
              left: "center",
              top: "middle",
              style: {
                text: "图表渲染失败，请刷新页面重试",
                fontSize: 14,
                fill: "#ff4d4f",
              },
            },
          },
          true
        );
      }
    } catch (fallbackError) {
      logger.error("设置错误状态图表时也失败:", fallbackError);
    }
  }
};

// 从后端获取通话记录的核心函数
const fetchCallRecords = async () => {
  loading.value = true;
  try {
    // 获取当前用户信息
    const currentUser = store.getters.getUser;
    const userRole = currentUser?.role || "";
    const userId = currentUser?.id;

    logger.info("当前用户信息:", currentUser);
    logger.info("用户角色:", userRole);
    logger.info("用户ID:", userId);

    // 添加更详细的调试信息
    console.log("=== 调试信息 ===");
    console.log("store.getters.getUser:", store.getters.getUser);
    console.log("store.getters.getUserRole:", store.getters.getUserRole);
    console.log("currentUser?.role:", currentUser?.role);
    console.log("userRole:", userRole);
    console.log("==================");

    // 将显示名转换为用户名再进行过滤
    const contactParam =
      getUsernameByDisplayName(searchForm.contactName) ||
      searchForm.contactName ||
      undefined;
    const params = {
      page: currentPage.value,
      pageSize: pageSize.value,
      deviceName: searchForm.deviceName || undefined,
      status: searchForm.status || undefined,
      startTime: searchForm.startTime || undefined,
      endTime: searchForm.endTime || undefined,
      device_id: device_id.value || undefined, // 添加设备ID过滤
      contact_name: contactParam, // 处理人员筛选（显示名→用户名）
      // 传递用户角色信息，由后端根据角色返回相应数据
      user_role: userRole,
      user_id: userId,
      tenant_id: isSuperAdmin.value
        ? searchForm.tenantId || undefined
        : undefined,
    };

    logger.info("获取通话记录，用户角色:", userRole, "用户ID:", userId);

    // 并行获取通话记录和统计数据
    const [recordsResponse, statsResponse] = await Promise.all([
      DataService.getCallRecords(params),
      DataService.getCallRecordsStats(params),
    ]);

    // 处理通话记录数据
    const responseData = (recordsResponse as any).data || recordsResponse;
    const { code, data, message } = responseData;

    if (code === 0) {
      // 后端返回的是items，不是records
      records.value = data.items || data.records || [];
      if (showContactName.value) {
        await ensureUserDisplayMap();
      }
      totalRecords.value = data.total;

      // 调试日志：打印第一条记录的结构
      if (records.value.length > 0) {
        console.log("第一条记录结构:", records.value[0]);
      }
    } else {
      ElMessage.error(message || "获取通话记录失败");
    }

    // 处理统计数据
    logger.info("原始统计数据响应:", statsResponse);

    // 检查响应结构
    if (statsResponse && typeof statsResponse === "object") {
      const statsResponseData = (statsResponse as any).data || statsResponse;
      const statsCode = statsResponseData.code;
      const statsResult = statsResponseData.data || statsResponseData;

      logger.info("统计数据响应数据:", statsResponseData);
      logger.info("统计数据代码:", statsCode);
      logger.info("统计数据结果:", statsResult);

      if (statsCode === 0 && statsResult) {
        // 更新统计数据
        Object.assign(statsData, statsResult);
        logger.info("统计数据更新成功:", statsResult);
        logger.info("更新后的statsData:", statsData);
      } else {
        logger.warn("获取统计数据失败，使用默认值");
        logger.warn("statsResponseData:", statsResponseData);
        logger.warn("statsCode:", statsCode);
        logger.warn("statsResult:", statsResult);
      }
    } else {
      logger.error("统计数据响应格式错误:", statsResponse);
    }
  } catch (error) {
    logger.error("获取通话记录时发生错误:", error);
    ElMessage.error("网络错误，无法加载通话记录");
  } finally {
    loading.value = false;
  }
};

// 加载用户名到显示名映射
const ensureUserDisplayMap = async () => {
  if (Object.keys(userDisplayMap.value).length > 0) return;
  try {
    const resp = await UserServiceApi.listUsers({});
    const items = (resp as any)?.data?.data?.items || [];
    const map: Record<string, string> = {};
    const rmap: Record<string, string> = {};
    for (const u of items) {
      const uname = (u as any)?.username;
      const dname = (u as any)?.display_name;
      if (uname) map[uname] = dname || uname;
      if (dname) rmap[dname] = uname;
    }
    userDisplayMap.value = map;
    displayToUserMap.value = rmap;
  } catch (e) {
    // 忽略
  }
};

const getDisplayName = (username: string) => {
  if (!username) return "—";
  const cu = store.getters.getUser;
  if (cu?.username === username && cu?.display_name) return cu.display_name;
  return userDisplayMap.value[username] || username;
};

// 根据显示名称反查用户名（用于筛选）
const getUsernameByDisplayName = (display: string) => {
  if (!display) return "";
  return displayToUserMap.value[display] || "";
};

// 加载租户列表（仅 super_admin）
const loadTenants = async () => {
  if (!isSuperAdmin.value) return;
  try {
    const resp = await TenantServiceApi.listTenants({ page_size: 100 });
    const payload = (resp as any)?.data?.data;
    const items = Array.isArray(payload?.items) ? payload.items : [];
    tenants.value = items.map((t: any) => ({ id: t.id, name: t.name }));
  } catch (e) {
    // 忽略加载失败
  }
};

// 下拉展开时按需加载
const onTenantDropdownVisible = (v: boolean) => {
  if (v && tenants.value.length === 0) {
    loadTenants();
  }
};

// 搜索按钮点击事件
const searchRecords = () => {
  currentPage.value = 1;
  fetchCallRecords();
  fetchMonthlyCallStats();
};

// 重置搜索条件
const resetSearch = () => {
  searchForm.deviceName = "";
  searchForm.status = "";
  searchForm.dateRange = [];
  searchForm.startTime = "";
  searchForm.endTime = "";
  searchForm.contactName = ""; // 重置处理人员筛选
  searchForm.tenantId = ""; // 重置租户筛选
  searchRecords(); // 重置后重新搜索
};

// 处理日期范围变化
const handleDateChange = (dates: string[] | null) => {
  if (dates && dates.length === 2) {
    searchForm.startTime = dates[0];
    searchForm.endTime = dates[1];
  } else {
    searchForm.startTime = "";
    searchForm.endTime = "";
  }
};

// 监听分页变化
watch([currentPage, pageSize], fetchCallRecords);

// 监听设备名称变化，更新图表
watch(
  () => searchForm.deviceName,
  () => {
    fetchMonthlyCallStats();
  }
);

// 角色就绪后自动加载租户
watch(
  () => isSuperAdmin.value,
  (v) => {
    if (v && tenants.value.length === 0) {
      loadTenants();
    }
  }
);

// 处理数据源变化
const handleDataSourceChange = () => {
  logger.info("🔄 检测到数据源变化，重新加载通话记录");
  fetchCallRecords();
  fetchMonthlyCallStats(); // 同时更新图表
};

// 组件挂载时，自动加载第一页数据
onMounted(async () => {
  // 初始化图表
  initChart();

  // 获取通话记录（用户信息已在NavView中加载）
  // 若从设备详情跳转携带了 device_id，则默认筛选本月
  if (device_id.value) {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    searchForm.dateRange = [
      start.toISOString().slice(0, 19).replace("T", " "),
      end.toISOString().slice(0, 19).replace("T", " "),
    ];
    searchForm.startTime = searchForm.dateRange[0];
    searchForm.endTime = searchForm.dateRange[1];
  }
  fetchCallRecords();

  // 获取月度统计数据
  fetchMonthlyCallStats();

  // 监听数据源变化事件
  window.addEventListener("dataSourceChanged", handleDataSourceChange);

  // super_admin 预加载租户列表
  if (isSuperAdmin.value) {
    await loadTenants();
  }
});

onUnmounted(() => {
  window.removeEventListener("dataSourceChanged", handleDataSourceChange);
  // 销毁图表实例
  if (chartInstance.value) {
    chartInstance.value.dispose();
  }
});

// 获取统计标题
const getStatsTitle = () => {
  if (
    !searchForm.deviceName &&
    !searchForm.status &&
    !searchForm.dateRange.length
  ) {
    return "当前显示最近一个月的通话记录统计";
  } else {
    return "当前显示搜索结果的通话记录统计";
  }
};

// 获取状态类型
const getStatusType = (status: string) => {
  const typeMap: Record<string, "success" | "info" | "danger" | "warning"> = {
    active: "warning",
    ended: "success",
    cancelled: "info",
    system_timeout: "danger",
    admin_forced_end: "warning",
    failed: "danger",
    // 兼容旧值
    connected: "success",
    missed: "info",
    ongoing: "warning",
    unanswered: "info",
    no_answer: "info",
  };
  return typeMap[status] || "info";
};

// 获取状态文本
const getStatusText = (status: string) => {
  const textMap: Record<string, string> = {
    active: "进行中",
    ended: "已完成",
    cancelled: "取消",
    system_timeout: "系统超时",
    admin_forced_end: "管理员结束",
    failed: "失败",
    // 兼容旧值
    connected: "已完成",
    missed: "取消",
    ongoing: "进行中",
    unanswered: "取消",
    no_answer: "取消",
  };
  return textMap[status] || "未知";
};

// 查看记录详情
const viewRecord = async (record: any) => {
  console.log("查看记录详情:", record); // 调试日志

  try {
    // 调用API获取详细的事件信息和日志
    const response = await DataService.getEventDetail(record.event_id);
    console.log("事件详情API响应:", response); // 调试日志

    if (!response || response.data?.code !== 0) {
      ElMessage.error("获取事件详情失败");
      return;
    }

    const eventDetail = response.data.data;
    const event = eventDetail.event;
    const logs = eventDetail.logs || [];

    // 构建详情信息
    const details = {
      deviceName: record.deviceName || record.device_id,
      deviceId: event.device_id,
      eventId: event.event_id,
      callTime: record.callTime || event.start_time,
      status: getStatusText(event.status),
      duration: formatDuration(event.start_time, event.end_time),
      contactName: event.contact_name,
      startTime: event.start_time,
      endTime: event.end_time,
      logs: logs,
    };

    console.log("详情信息:", details); // 调试日志

    // 构建日志HTML
    const logsHtml =
      logs.length > 0
        ? `
      <div style="margin-top: 15px;">
        <h4 style="margin-bottom: 10px; color: #409EFF;">操作日志</h4>
        <div style="max-height: 300px; overflow-y: auto; border: 1px solid #e4e7ed; border-radius: 4px; padding: 10px;">
          ${logs
            .map(
              (log: any) => `
            <div style="margin-bottom: 8px; padding: 8px; background: #f5f7fa; border-radius: 4px; border-left: 3px solid #409EFF;">
              <div style="font-weight: bold; color: #303133;">${
                log.action
              }</div>
              <div style="font-size: 12px; color: #909399; margin-top: 2px;">
                操作者: ${log.operator} | 时间: ${new Date(
                log.created_at
              ).toLocaleString()}
              </div>
              ${
                log.message
                  ? `<div style="font-size: 12px; color: #606266; margin-top: 2px;">${log.message}</div>`
                  : ""
              }
            </div>
          `
            )
            .join("")}
        </div>
      </div>
    `
        : "";

    // 显示详情对话框
    await ElMessageBox.confirm(
      `
      <div style="text-align: left;">
        <p><strong>设备名称:</strong> ${details.deviceName}</p>
        <p><strong>设备编号:</strong> ${details.deviceId}</p>
        <p><strong>事件ID:</strong> ${details.eventId}</p>
        <p><strong>通话时间:</strong> ${details.callTime}</p>
        <p><strong>通话状态:</strong> ${details.status}</p>
        <p><strong>通话时长:</strong> ${details.duration}</p>
        ${
          showContactName.value
            ? `<p><strong>处理人员:</strong> ${
                details.contactName || "未知"
              }</p>`
            : ""
        }
        <p><strong>开始时间:</strong> ${details.startTime}</p>
        <p><strong>结束时间:</strong> ${details.endTime}</p>
        ${logsHtml}
      </div>
      `,
      "通话记录详情",
      {
        dangerouslyUseHTMLString: true,
        confirmButtonText: "确定",
        cancelButtonText: "关闭",
        customClass: "call-record-details-dialog",
      }
    );
  } catch (error) {
    // 忽略取消操作的错误
    if (error !== "cancel" && error !== "close") {
      console.error("弹窗错误:", error);
      ElMessage.error("获取事件详情失败");
    }
  }
};

// 播放记录：调用录音直链并在对话框播放
const playRecord = async (record: any) => {
  try {
    if (record.status !== "connected" && record.status !== "ended") {
      ElMessage.warning("No playable content for this record");
      return;
    }

    let recordingId: string | undefined =
      record.recording_id || record.recordingId;
    if (!recordingId) {
      const baseUrl = `${config.apiBaseUrl}/recordings`;
      const client = (await import("@/services/apiClient")).default;
      const startDate = (record.start_time || "").slice(0, 10);
      const resp = await client.get(baseUrl, {
        params: {
          page: 1,
          size: 20,
          device_id: record.device_id || record.deviceId,
          start_date: startDate || undefined,
          end_date: startDate || undefined,
        },
      });
      const payload = resp?.data?.data || resp?.data || {};
      const list: any[] = Array.isArray(payload.recordings)
        ? payload.recordings
        : [];
      console.log("获取到的录音列表:", list); // 调试日志
      const callStart = record.start_time
        ? new Date(record.start_time).getTime()
        : NaN;
      let best: any | null = null;
      let bestDelta = Number.POSITIVE_INFINITY;
      for (const r of list) {
        if (r?.status !== "completed") continue;
        const st = r?.start_time ? new Date(r.start_time).getTime() : NaN;
        if (!isNaN(st) && !isNaN(callStart)) {
          const delta = Math.abs(st - callStart);
          if (delta < bestDelta) {
            bestDelta = delta;
            best = r;
          }
        }
      }
      if (best) recordingId = best.recording_id || best.id;
    }

    console.log("最终使用的录音ID:", recordingId); // 调试日志

    if (!recordingId) {
      ElMessage.error("Recording not found for this call");
      return;
    }

    const baseUrl2 = `${config.apiBaseUrl}/recordings`;
    const client2 = (await import("@/services/apiClient")).default;
    const resp2 = await client2.get(`${baseUrl2}/${recordingId}/download-url`);
    const url = resp2?.data?.data?.url || resp2?.data?.url;
    console.log("获取到的播放URL:", url); // 调试日志
    if (!url) {
      ElMessage.error("Failed to get playback url");
      return;
    }

    currentAudio.url = url;
    currentAudio.title = record.deviceName || record.device_id || recordingId;
    playDialogVisible.value = true;
    await nextTick();
    try {
      const audio = audioPlayer.value;
      if (audio) {
        audio.src = url;
        await audio.play();
      }
    } catch (e) {
      console.warn("Auto play failed; user interaction may be required.", e);
    }
  } catch (error) {
    console.error("播放失败:", error);
    ElMessage.error("Play failed");
  }
};

// 导出记录
const exportRecords = () => {
  ElMessage.success("通话记录导出功能");
};

// 显示设备本月统计
const showDeviceStats = async (record: any) => {
  try {
    const deviceName = record.deviceName || record.device_id;

    // 设置设备名称筛选
    searchForm.deviceName = deviceName;

    // 更新图表描述
    logger.info(`显示设备 ${deviceName} 的本月统计`);

    // 重新获取该设备的月度统计数据
    await fetchMonthlyCallStats();

    ElMessage.success(`已切换到设备 ${deviceName} 的本月统计`);
  } catch (error) {
    logger.error("显示设备统计失败:", error);
    ElMessage.error("获取设备统计失败");
  }
};

// 重置图表筛选条件
const resetChartFilter = async () => {
  try {
    // 清除设备筛选
    searchForm.deviceName = "";

    // 重新获取所有设备的月度统计数据
    await fetchMonthlyCallStats();

    ElMessage.success("已重置为所有设备的统计");
  } catch (error) {
    logger.error("重置图表筛选失败:", error);
    ElMessage.error("重置失败");
  }
};

// 返回设备管理页面
const goBack = () => {
  router.push({ name: "设备管理" });
};

// 获取页面标题
const pageTitle = computed(() => {
  if (route.params.device_id) {
    return `通话记录 - ${route.params.device_id}`;
  }
  return "通话记录";
});

// 获取设备ID
const device_id = computed(() => {
  return route.params.device_id as string;
});

// 获取当前用户权限范围提示
const permissionHint = computed(() => {
  const currentUser = store.getters.getUser;
  const userRole = currentUser?.role || "";

  switch (userRole) {
    case ROLE_OPERATOR:
      return "当前显示您处理过的通话记录";
    case ROLE_ADMIN:
      return "当前显示所有通话记录";
    case ROLE_SUPER_ADMIN:
      return "当前显示所有通话记录";
    default:
      return "当前显示通话记录";
  }
});

// 获取当前用户角色
const currentUserRole = computed(() => {
  const currentUser = store.getters.getUser;
  return currentUser?.role || "";
});

// 是否为 super_admin
const isSuperAdmin = computed(() => currentUserRole.value === ROLE_SUPER_ADMIN);

// 判断是否显示contact名字（admin和super_admin显示）
const showContactName = computed(() => {
  return (
    currentUserRole.value === ROLE_ADMIN ||
    currentUserRole.value === ROLE_SUPER_ADMIN
  );
});

// 判断是否可以筛选处理人员
const canFilterByContactName = computed(() => {
  return (
    currentUserRole.value === ROLE_ADMIN ||
    currentUserRole.value === ROLE_SUPER_ADMIN
  );
});

// 格式化通话时长
const formatDuration = (startTime: string, endTime: string) => {
  if (!startTime || !endTime) {
    return "未知";
  }

  try {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end.getTime() - start.getTime();

    if (durationMs <= 0) {
      return "0秒";
    }

    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}小时${minutes % 60}分钟`;
    } else if (minutes > 0) {
      return `${minutes}分钟${seconds % 60}秒`;
    } else {
      return `${seconds}秒`;
    }
  } catch (error) {
    return "格式错误";
  }
};

// (其余静态方法暂时保留，但需要审阅)
</script>

<style lang="scss" scoped>
.call-records {
  padding: 8px; // 进一步缩小外边距
  background-color: #f5f7fa;
  height: 100vh;
  overflow: hidden; /* 防止双滚动条 */
}

// 主内容区域布局
.main-content {
  display: flex;
  flex-direction: column;
  gap: 6px; // 组件之间更紧凑
  margin-bottom: 6px;
  height: auto;
}

.chart-section {
  order: 0; // 先显示图表
}

.records-section {
  order: 1; // 再显示记录表格
  min-width: 0; // 防止flex项目溢出
}

.chart-card {
  height: 300px; // 进一步缩小，给表格留更多空间
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.chart-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

.chart-info {
  margin-bottom: 0; // 紧贴图表
}

.chart-description {
  display: none; // 不显示旧描述
}

.chart-title {
  margin: 0;
  font-size: 18px; // 更接近折线图标题尺寸
  font-weight: 600;
  color: #303133;
}

.chart {
  flex: 1;
  min-height: 280px; // 进一步减少，压缩图表空间
  overflow: hidden;
}

.chart-actions {
  position: absolute;
  right: 12px;
  top: 48%;
  transform: translateY(-48%);
}

/* 筛选区换行挤压问题：保证按钮区域不被上方卡片遮挡 */
.search-section .el-card {
  margin-bottom: 0px;
}
.search-section .el-row {
  row-gap: 2px;
}

// 详情对话框样式
:deep(.call-record-details-dialog) {
  .el-message-box__content {
    max-height: 400px;
    overflow-y: auto;
  }

  .el-message-box__message {
    line-height: 1.6;

    p {
      margin: 8px 0;
      padding: 4px 0;
      border-bottom: 1px solid #f0f0f0;

      &:last-child {
        border-bottom: none;
      }
    }
  }
}

.page-header {
  margin-bottom: 24px;

  h2 {
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    color: #303133;
  }

  .page-actions {
    display: flex;
    gap: 12px;
  }

  .permission-hint {
    margin-top: 8px;
  }
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 15px; // 减少间距
  margin-bottom: 6px; // 与其他组件间距统一
}

.stat-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  font-size: 24px;
  color: white;
}

.stat-card:nth-child(1) .stat-icon {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.stat-card:nth-child(2) .stat-icon {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.stat-card:nth-child(3) .stat-icon {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.stat-card:nth-child(4) .stat-icon {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
}

.stat-content {
  flex: 1;
}

.stat-number {
  font-size: 28px;
  font-weight: bold;
  color: #333;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 14px;
  color: #666;
}

.search-section {
  margin-bottom: 6px; // 与图表和表格间距统一
}

.search-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.search-form {
  padding: 6px 0; // 降低筛选栏行高
}

/* 保证搜索按钮不被遮挡 */
.search-form .el-row {
  align-items: flex-start;
  flex-wrap: wrap;
}
.search-form .el-col {
  min-height: 32px; // 更小的列高度
  margin-bottom: 6px;
  display: flex;
  align-items: center; /* 与时间选择器垂直居中对齐 */
}
.button-group {
  display: flex;
  gap: 8px;
  align-items: center; /* 垂直居中，避免与输入框上下不齐 */
  margin-top: 0; /* 去除向下偏移 */
}

.search-info {
  margin-top: 15px;
}

.records-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 表格通话记录卡片头部直接贴顶 */
.records-card :deep(.el-card__body) {
  padding: 0 !important; /* 去掉卡片内边距（含顶部） */
  margin: 0 !important;
}

/* 移除表格顶部额外空间（部分主题为.el-table有默认margin） */
:deep(.records-card .el-table) {
  margin-top: 0 !important; /* 移除Element默认顶部间距 */
  border-top: none !important; /* 避免顶部边框造成视觉空隙 */
}

/* 部分主题在表头容器上加了额外间距，这里强制归零 */
:deep(.records-card .el-table__header-wrapper) {
  margin-top: 0 !important;
}

/* 去掉卡片与上一块之间的缝隙 */
.records-section {
  margin-top: 0 !important;
}

/* 确保卡片本身不引入额外外边距 */
:deep(.records-section .el-card) {
  margin-top: 0 !important;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.ym-label {
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 4px;
  background: #eef2ff;
  color: #303133;
  font-size: 16px; // 与主标题大小一致
  line-height: 22px;
}

.ym-plain {
  margin-left: 8px;
  color: #303133;
  font-size: 24px; // 放大，和顶部统计数字同级感
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.quality-rating {
  display: flex;
  align-items: center;
  gap: 8px;
}

.quality-text {
  font-size: 12px;
  color: #666;
}

.action-buttons {
  display: flex;
  gap: 1px; // 减少按钮间距
  flex-wrap: wrap;
}

.action-buttons-center {
  display: flex;
  gap: 4px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
}

/* 表格列不换行（设备编号列） */
:deep(.nowrap-cell .cell) {
  white-space: nowrap;
}

.table-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

/* 表头紧凑与底色 */
:deep(.table-th-compact) .cell {
  line-height: 24px !important;
  padding-top: 2px !important;
  padding-bottom: 2px !important;
}
:deep(.el-table__header th) {
  background: #f7f8fb !important;
}

/* 按钮图标间距调整 */
.el-button .el-icon {
  margin-right: 6px;
}

.el-button .el-icon:only-child {
  margin-right: 0;
}

/* 按钮组样式 */
.button-group {
  display: flex;
  gap: 8px;
  align-items: center;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .main-content {
    flex-direction: column;
    height: auto;
  }

  .records-section {
    flex: 1;
  }

  .chart-section {
    min-width: auto;
    margin-left: 0;
  }

  .chart-container {
    height: 450px; // 增加高度
  }

  .chart {
    min-height: 400px; // 增加最小高度
  }
}

@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .chart-container {
    height: 250px;
  }

  .chart {
    min-height: 200px;
  }
}

// 播放对话框样式
.audio-player-container {
  padding: 20px;
  text-align: center;
}

.audio-info {
  margin-bottom: 20px;

  h4 {
    margin: 0;
    color: #303133;
    font-size: 16px;
    font-weight: 500;
  }
}

.audio-controls {
  width: 100%;

  audio {
    width: 100%;
    height: 40px;
  }
}

.dialog-footer {
  text-align: center;
}
</style>
